import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import PlusIcon from "../../../../../../../../icons/Plus";
import SortIcon from "../../../../../../../../icons/Sort";
import TrashIcon from "../../../../../../../../icons/Trash";
import { FormFieldOption, newFieldOption } from "../../../../../../../../model/formFields";
import { classNames } from "../../../../../../../../utils/styles";
import { ListCardAction, VisibilityIcon } from "../../../../../../components/ListCard";
import { Container } from "./styles";

type Props = {
  options: FormFieldOption[];
  onChange: (options: FormFieldOption[]) => void;
};

/** Options of a selection field: sortable rows with inline text, show/hide and remove, like the fields list. */
export const OptionsList = ({ options, onChange }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.form.field.options";

  // Visitors need at least two options: the last two visible ones can't be hidden or removed
  const visibleCount = options.filter(option => option.active !== false).length;
  const minimumLabel = t(`${prefix}.minimum`);

  const update = (id: string, change: Partial<FormFieldOption>) =>
    onChange(options.map(option => (option.id === id ? { ...option, ...change } : option)));

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (!destination) return;
    const sorted = Array.from(options);
    const [moved] = sorted.splice(source.index, 1);
    sorted.splice(destination.index, 0, moved);
    onChange(sorted);
  };

  return (
    <Container>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="options" direction="vertical">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {options.map((option, index) => {
                const visible = option.active !== false;
                const isLocked = visible && visibleCount <= 2;
                const toggleLabel = isLocked ? minimumLabel : visible ? t(`${prefix}.hide`) : t(`${prefix}.show`);
                const removeLabel = isLocked ? minimumLabel : t(`${prefix}.remove`);
                return (
                  <Draggable key={option.id} index={index} draggableId={option.id}>
                    {provided => (
                      <div className="item" ref={provided.innerRef} {...provided.draggableProps}>
                        <div className={classNames("row", { hidden: !visible })}>
                          <span className="drag-handle" {...provided.dragHandleProps} aria-hidden="true">
                            <SortIcon color="currentColor" />
                          </span>
                          <input
                            type="text"
                            maxLength={60}
                            value={option.label}
                            aria-label={t(`${prefix}.placeholder`, { number: index + 1 })}
                            placeholder={t(`${prefix}.placeholder`, { number: index + 1 })}
                            onChange={e => update(option.id, { label: e.target.value })}
                          />
                          <ListCardAction
                            type="button"
                            title={toggleLabel}
                            aria-label={toggleLabel}
                            disabled={isLocked}
                            onClick={() => update(option.id, { active: !visible })}>
                            <VisibilityIcon visible={visible} />
                          </ListCardAction>
                          <ListCardAction
                            type="button"
                            $danger
                            title={removeLabel}
                            aria-label={removeLabel}
                            disabled={isLocked}
                            onClick={() => onChange(options.filter(({ id }) => id !== option.id))}>
                            <TrashIcon size={16} color="currentColor" />
                          </ListCardAction>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder as ReactNode}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <button type="button" className="add" onClick={() => onChange([...options, newFieldOption()])}>
        <PlusIcon width={11} height={11} color="currentColor" />
        {t(`${prefix}.add`)}
      </button>
    </Container>
  );
};
